/**
 * The bar that says this tab is HQ viewing an agent's CIDAS, not the agent.
 *
 * Shown on every screen while a view session is open (api/client.js `viewAs`),
 * on the phone and the desktop build alike. It counts the session down and ends
 * it early on request, and tells the server when the tab goes away so that
 * closing the tab ends the view too. Everything else about the view is enforced by the agent
 * server: any change is refused there, whatever a screen offers.
 *
 * Phone build: a slim bar across the top that RESERVES its strip (design.css,
 * `view-as-active`) rather than floating over the page — a floating pill sat on
 * Home's cards, the notification rows and the Notes thread, and a 390px screen
 * had no room left for the countdown. Desktop: a pill at the bottom centre,
 * where the wide pages leave space.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import client, { BASE, viewAs, endViewInTab } from '../api/client';
import useIsDesktop from '../hooks/useIsDesktop';

const minutesLeft = (iso) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 60000));

export default function ViewAsBanner() {
  const v = viewAs.get();
  const expiresAt = v?.session?.expires_at;
  const [left, setLeft] = useState(() => (expiresAt ? minutesLeft(expiresAt) : 0));
  const [ending, setEnding] = useState(false);
  const desktop = useIsDesktop();

  useEffect(() => {
    if (!expiresAt) return undefined;
    const tick = () => {
      const m = minutesLeft(expiresAt);
      setLeft(m);
      if (m <= 0) { endViewInTab(); return; }
      // Ended from the admin portal? A 401 here sends the tab to the ended page
      // (api/client.js), so an idle screen does not go on saying "N min left".
      // It is also the heartbeat that tells the server this tab is still open.
      client.get('/api/agent/me').catch(() => {});
    };
    const t = setInterval(tick, 15000);
    return () => clearInterval(t);
  }, [expiresAt]);

  // Closing the tab ends the view. The page cannot tell a close from a reload, so
  // this only says "closing"; a reload's first request cancels it and a closed
  // tab never does (cid-agent-server viewAs.service). A beacon has no headers,
  // so the token travels as the plain-text body.
  useEffect(() => {
    const token = v?.accessToken;
    if (!token) return undefined;
    const bye = () => {
      try { navigator.sendBeacon(`${BASE}/api/agent/auth/view-as/closing`, new Blob([token], { type: 'text/plain' })); } catch { /* the silence backstop ends it */ }
    };
    window.addEventListener('pagehide', bye);
    return () => window.removeEventListener('pagehide', bye);
  }, [v?.accessToken]);

  // The phone bar takes its strip off the top of the app (see design.css).
  useEffect(() => {
    if (desktop || !v?.accessToken) return undefined;
    document.body.classList.add('view-as-active');
    return () => document.body.classList.remove('view-as-active');
  }, [desktop, v?.accessToken]);

  if (!v?.accessToken) return null;

  const end = async () => {
    setEnding(true);
    try { await client.post('/api/agent/auth/view-as/end'); } catch { /* ends on expiry anyway */ }
    endViewInTab();
  };

  const a = v.agent || {};
  const name = a.display_name || a.full_name || 'this agent';

  const endButton = (
    <button
      type="button"
      onClick={end}
      disabled={ending}
      style={{ flex: 'none', height: desktop ? 28 : 26, padding: '0 12px', borderRadius: 999, border: 0, background: '#fff', color: '#1f1147', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
    >
      {ending ? 'Ending…' : 'End view'}
    </button>
  );

  // Portalled to <body>: on the phone #root is shifted down to make room for the
  // bar, and its transform would pin the bar inside that shifted box.
  if (!desktop) {
    return createPortal(
      <div role="status" data-tour="view-as-banner" className="view-as-bar">
        {/* Only the name gives way on a narrow screen; the countdown always shows. */}
        <b style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Viewing as {name}</b>
        <span style={{ flex: 'none', opacity: 0.75, whiteSpace: 'nowrap' }}>· read only · {left} min left</span>
        <span style={{ flex: 1 }} />
        {endButton}
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      role="status"
      data-tour="view-as-banner"
      style={{
        position: 'fixed', left: '50%', transform: 'translateX(-50%)', zIndex: 7000, bottom: 24,
        display: 'flex', alignItems: 'center', gap: 10, maxWidth: 'calc(100vw - 24px)',
        padding: '8px 8px 8px 14px', borderRadius: 999,
        background: '#1f1147', color: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,.28)',
        fontSize: 12.5, lineHeight: 1.3,
      }}
    >
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <b>Viewing as {name}</b>
        <span style={{ opacity: 0.75 }}> · read only · {left} min left</span>
      </span>
      {endButton}
    </div>,
    document.body,
  );
}
